import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { DEFAULT_RATIO, bucketRatio, hashSeed, roughGeometry, roughMask } from './paintShapes';

// Dolmenwood RoughEdge: one shared observer, measuring the unmasked parent.
// Sibling hover/selection layers share the same measurement and silhouette.
const listeners = new Map();
let observer;
function observe(host, measure) {
  let callbacks = listeners.get(host);
  if (!callbacks) { callbacks = new Set(); listeners.set(host, callbacks); }
  callbacks.add(measure);
  observer ??= new ResizeObserver(entries => {
    for (const entry of entries) {
      const box = entry.borderBoxSize?.[0];
      for (const notify of listeners.get(entry.target) || []) {
        notify(box?.inlineSize ?? entry.target.offsetWidth, box?.blockSize ?? entry.target.offsetHeight);
      }
    }
  });
  observer.observe(host);
  return () => {
    const callbacks = listeners.get(host);
    callbacks?.delete(measure);
    if (callbacks?.size === 0) { listeners.delete(host); observer.unobserve(host); }
  };
}

// Dolmenwood paint-grain.ts: fixed pixel scale and full-seed texture offset.
export function paintGrain(variant, seed) {
  const coarse = variant === 'wash';
  const size = coarse ? 256 : 160;
  const key = typeof seed === 'number' ? seed : hashSeed(seed);
  return {
    '--lab-paint-grain': `url("/mastery/laboratory/row-paint/bristle-${coarse ? 'coarse' : 'fine'}.webp")`,
    '--lab-grain-size': `${size}px ${size}px`,
    '--lab-grain-pos': `${(key % size) - size / 2}px ${((key >>> 8) % size) - size / 2}px`,
  };
}

export default function PotionPaint({ seed, variant = 'wash', className = '', draw = false }) {
  const ref = useRef(null);
  const [ratio, setRatio] = useState(DEFAULT_RATIO);
  // Dolmenwood InkPlate captures the click at mount; resize never restarts it.
  const [animate] = useState(draw);
  useLayoutEffect(() => {
    const host = ref.current?.parentElement;
    if (!host) return;
    const measure = (width, height) => setRatio(bucketRatio(width / height));
    measure(host.offsetWidth, host.offsetHeight);
    if (typeof ResizeObserver !== 'undefined') return observe(host, measure);
  }, []);
  const key = typeof seed === 'number' ? seed : hashSeed(seed);
  return <span ref={ref} aria-hidden="true"
    className={`vh-lab-paint absolute pointer-events-none ${className}${animate ? ' vh-lab-ink-draw' : ''}`}
    style={{ ...roughGeometry(variant, key, ratio).inset, '--lab-paint-mask': roughMask(variant, key, ratio), ...paintGrain(variant, key) }} />;
}
